import { useSession } from '@/components/Session/use-session'
import { wsSubscriptionManager } from '@/lib/socket/WebSocketSubscriptionManager'
import { API_WS_URL } from '@shared/api/external-api'
import { EventType } from '@unx/shared/types/websocket'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

const WS_URL = API_WS_URL ?? ''

interface WebSocketContextType {
  isConnected?: boolean
  logoutWebsocket: () => void
  addSubscription: (
    events: EventType[],
    handler: (msg: MessageEvent<unknown>) => void,
  ) => void
  removeSubscription: (events: EventType[]) => void
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined,
)

interface WebSocketProviderProps {
  children: React.ReactNode
}

type WebSocketSubscription = Record<
  string,
  (event: MessageEvent<unknown>) => void
>

export const WebSocketProvider = ({ children }: WebSocketProviderProps) => {
  const socket = useRef<WebSocket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const isLoggedIn = useRef(false)
  const isLoginPending = useRef(false)
  const subscriptions = useRef<WebSocketSubscription>({})
  const accessTokenRef = useRef<string | undefined>(undefined)

  const { session } = useSession()
  const accessToken = session?.accessToken

  // Keep accessToken in ref to avoid triggering reconnects
  useEffect(() => {
    accessTokenRef.current = accessToken
  }, [accessToken])

  // Refs for stable callbacks in useEffect
  const reconnectWebSocketRef = useRef<() => WebSocket>(
    () => new WebSocket(WS_URL),
  )
  const loginWebsocketRef = useRef<() => void>(() => {})

  const getSubscriptionKey = (events: EventType[]) => {
    return events.join(',')
  }

  const resetWebSocketState = useCallback(() => {
    isLoggedIn.current = false
    isLoginPending.current = false
    setIsConnected(false)
    subscriptions.current = {}
    wsSubscriptionManager.setLoginState(false)
  }, [])

  const isWebSocketReady = useCallback(() => {
    return socket.current && socket.current.readyState === WebSocket.OPEN
  }, [])

  const loginWebsocket = useCallback(() => {
    if (!isWebSocketReady()) {
      return
    }

    const token = accessTokenRef.current
    if (token && !isLoginPending.current) {
      isLoginPending.current = true
      socket.current?.send(JSON.stringify({ method: 'login', token }))
    }
  }, [isWebSocketReady])

  // Update ref whenever the function changes
  loginWebsocketRef.current = loginWebsocket

  const logoutWebsocket = useCallback(() => {
    if (!isWebSocketReady() || !isLoggedIn.current) return

    socket.current?.send(JSON.stringify({ method: 'logout' }))
    resetWebSocketState()
  }, [isWebSocketReady, resetWebSocketState])

  const globalMessageHandler = useCallback(
    (event: MessageEvent<unknown>) => {
      const message = JSON.parse(event.data as string)
      const key = message.event as string

      if (message.method === 'login') {
        isLoginPending.current = false

        if (message.status === 'success') {
          isLoggedIn.current = true
          setIsConnected(true)
          wsSubscriptionManager.setLoginState(true)
        } else {
          resetWebSocketState()
        }
      }

      if (subscriptions.current[key]) {
        subscriptions.current[key](event)
      }
    },
    [resetWebSocketState],
  )

  const reconnectWebSocket = useCallback(() => {
    if (socket.current) {
      socket.current.close()
    }

    const wsBase = new WebSocket(WS_URL)
    socket.current = wsBase

    const handleClose = () => {
      resetWebSocketState()
    }

    const handleError = () => {
      resetWebSocketState()
    }

    const handleOpen = () => {
      // Use accessToken from ref to avoid dependency issues
      const token = accessTokenRef.current
      if (token && !isLoggedIn.current && !isLoginPending.current) {
        isLoginPending.current = true
        wsBase.send(JSON.stringify({ method: 'login', token }))
      }
    }

    wsBase.addEventListener('message', globalMessageHandler)
    wsBase.addEventListener('close', handleClose)
    wsBase.addEventListener('error', handleError)
    wsBase.addEventListener('open', handleOpen)

    return wsBase
  }, [resetWebSocketState, globalMessageHandler])

  // Update refs for use in useEffect
  reconnectWebSocketRef.current = reconnectWebSocket
  loginWebsocketRef.current = loginWebsocket

  // Setup visibility change callbacks in subscription manager
  useEffect(() => {
    wsSubscriptionManager.setVisibilityCallback((visible) => {
      if (!visible && isLoggedIn.current) {
        // Page hidden - logout
        socket.current?.send(JSON.stringify({ method: 'logout' }))
        isLoggedIn.current = false
        setIsConnected(false)
      }
    })

    wsSubscriptionManager.setReconnectCallback(() => {
      // Page visible again - reconnect if needed
      if (!socket.current || socket.current.readyState === WebSocket.CLOSED) {
        if (accessToken) {
          reconnectWebSocketRef.current?.()
        }
      } else if (
        socket.current.readyState === WebSocket.OPEN &&
        !isLoggedIn.current
      ) {
        if (accessToken) {
          loginWebsocketRef.current?.()
        }
      }
    })
  }, [accessToken])

  // Main WebSocket connection effect - only reconnects when truly needed
  useEffect(() => {
    if (!accessToken) {
      // Don't reconnect when logging out
      if (isLoggedIn.current) {
        resetWebSocketState()
      }
      return
    }

    // Only create connection once
    const wsBase = reconnectWebSocket()

    return () => {
      wsBase.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]) // Only depend on accessToken, not the functions

  // Separate effect for login/logout logic
  useEffect(() => {
    // Only attempt login if we have a token and websocket is ready
    if (accessToken && !isLoggedIn.current && isWebSocketReady()) {
      loginWebsocket()
    }
    // Don't call logoutWebsocket here - it's handled by the main effect's cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]) // Only depend on accessToken - removed isConnected to prevent loop

  const addSubscription = useCallback(
    (events: EventType[], handler: (msg: MessageEvent<unknown>) => void) => {
      if (!isWebSocketReady()) {
        return
      }

      const key = getSubscriptionKey(events)
      if (!subscriptions.current[key]) {
        events.forEach((event) => {
          socket.current?.send(
            JSON.stringify({ method: 'subscribe', events: [event] }),
          )
        })
        subscriptions.current[key] = handler
      }
    },
    [isWebSocketReady],
  )

  const removeSubscription = useCallback(
    (events: EventType[]) => {
      if (!isWebSocketReady()) return

      const key = getSubscriptionKey(events)

      if (subscriptions.current[key]) {
        events.forEach((event) => {
          socket.current?.send(
            JSON.stringify({ method: 'unsubscribe', events: [event] }),
          )
        })

        delete subscriptions.current[key]
      }
    },
    [isWebSocketReady],
  )

  const value = {
    isConnected,
    addSubscription,
    removeSubscription,
    logoutWebsocket,
  }

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error(
      'useWebSocketContext must be used within a WebSocketProvider',
    )
  }
  return context
}
